import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import {
  AlertNotification,
  Button,
  Col,
  H2,
  Input,
  Label,
  LabeledCheckbox,
  Loading,
  Row,
  Text,
} from '@commonsku/styles';

import { oauth } from '../utils';
import { getCompanies } from '../promostandards';

/** PromoStandards company-directory service type codes. */
export const PS_DIRECTORY_SERVICE_CODES = {
  PRODUCT_DATA: 'Product',
  PRICING_AND_CONFIGURATION: 'PPC',
};

export const PS_VALIDATOR_SERVICES = [
  {
    code: PS_DIRECTORY_SERVICE_CODES.PRODUCT_DATA,
    label: 'Product Data',
  },
  {
    code: PS_DIRECTORY_SERVICE_CODES.PRICING_AND_CONFIGURATION,
    label: 'Product Pricing and Configuration',
  },
];

export const PS_PPC_CONFIGURATION_TYPES = ['Blank', 'Decorated'];

export const PS_PPC_CURRENCIES = ['USD', 'CAD'];

export function parseFobPointChoices(json) {
  const choices = json?.fob_point_choices;
  return Array.isArray(choices) ? choices : [];
}

export const CREDENTIALS_MISSING =
  'Credentials are not present in the commonsku database. Contact the supplier to get PromoStandards credentials for commonsku.';

export function credentialsMissing(username, password) {
  return !username || !password;
}

/** Deduplicate report rows into supplier choices for the validator dropdown. */
export function getSupplierChoices(tenantEndpoints) {
  const byApi = {};
  Object.values(tenantEndpoints || {}).forEach((te) => {
    if (!te.promo_api_name) {
      return;
    }
    const existing = byApi[te.promo_api_name];
    if (!existing) {
        byApi[te.promo_api_name] = {
          promo_api_name: te.promo_api_name,
          ps_company_code: String(te.ps_company_code || '').trim(),
          commonsku_username: te.commonsku_username || '',
          commonsku_password: te.commonsku_password || '',
          label: te.division_name || te.supplier_name || te.display_name || te.promo_api_name,
        };
        return;
      }
      if (!existing.ps_company_code && te.ps_company_code) {
        existing.ps_company_code = String(te.ps_company_code).trim();
      }
    if (!existing.commonsku_username && te.commonsku_username) {
      existing.commonsku_username = te.commonsku_username;
      existing.commonsku_password = te.commonsku_password || '';
    }
  });
  return _.sortBy(Object.values(byApi), 'label');
}

export function pickEndpointUrl(endpoint, useTestUrl = false) {
  if (!endpoint) {
    return '';
  }
  return useTestUrl
    ? (endpoint.test_url || endpoint.live_url || '')
    : (endpoint.live_url || endpoint.test_url || '');
}

/** Fallback when promo.commonsku.com is missing a service (e.g. SanMar Product). */
export async function loadOfficialDirectoryEndpoint(psCompanyCode, serviceCode) {
  try {
    const { json } = await oauth('POST', 'promo-standards', {
      action: 'directory_endpoint',
      admin_test: true,
      ps_company_code: psCompanyCode,
      service_code: serviceCode,
    });
    return json?.endpoint || null;
  } catch (err) {
    return err?.json?.endpoint || null;
  }
}

/**
 * Load a directory endpoint by service code (Product, PPC, …).
 * Uses promo.commonsku.com/api/companies (same as the PS report).
 * Falls back to official PromoStandards directory if missing.
 */
export async function loadDirectoryEndpoint(psCompanyCode, serviceCode) {
  const companyCode = String(psCompanyCode || '').trim();
  let company = null;
  let endpoint = null;

  if ('' === companyCode) {
    return { company: null, endpoint: null };
  }

  try {
    // Do not pass `fields` — it returns an incomplete endpoints list
    // (e.g. SanMar only INV/PPC). Full URL shape:
    // https://promo.commonsku.com/api/companies?code__in=SanMar
    const data = await getCompanies({
      params: {
        code__in: companyCode,
      },
    });
    const list = Array.isArray(data) ? data : (data?.results || []);
    company = _.find(list, (c) =>
      String(c.code || '').trim().toLowerCase() === companyCode.toLowerCase()
    ) || list[0] || null;

    const matches = (company?.endpoints || []).filter((e) => e.code === serviceCode);
    if (matches.length > 0) {
      endpoint = _.maxBy(matches, (e) => e.version) || matches[0];
    }
  } catch (e) {
    // Fall through to official directory.
  }

  if (!endpoint) {
    endpoint = await loadOfficialDirectoryEndpoint(companyCode, serviceCode);
  }

  return { company, endpoint };
}

export function formatSoapOrJsonResponse({
  showXml,
  response,
  requestXml,
  responseXml,
}) {
  if (showXml) {
    if (!responseXml && !requestXml) {
      return 'No SOAP XML captured.';
    }
    return `--- request ---\n${requestXml || ''}\n\n--- response ---\n${responseXml || ''}`;
  }
  if (!response) {
    return 'No response yet.';
  }
  return JSON.stringify(response, null, 2);
}

const Page = styled.div`
  padding: 16px 24px 48px;
  max-width: 1100px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #555;
  max-width: 720px;
`;

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
  width: 100%;
`;

const Hint = styled.span`
  font-weight: 400;
  color: #888;
  font-size: 13px;
`;

const CopyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  &&& input {
    flex: 1;
    min-width: 0;
    margin: 0;
  }
`;

const NativeSelect = styled.select`
  &&& {
    margin: 0;
    height: 38px;
    width: 100%;
  }
`;

const FobLoadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const FobSelect = styled(NativeSelect)`
  &&& {
    width: 100px;
    max-width: 100%;
    margin: 0;
  }
`;

const Meta = styled.p`
  margin: ${(p) => (p.$spaced ? '0 0 12px' : '4px 0 0')};
  font-size: 12px;
  color: #666;
`;

const ResponseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ResponsePre = styled.pre`
  max-height: 70vh;
  overflow: auto;
  background: #f6f8fa;
  padding: 16px;
  font-size: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  margin: 0;
  border: 1px solid #e2e8ee;
  border-radius: 4px;
`;

const LoadingWrap = styled.div`
  text-align: center;
  padding: 24px;
`;

const ErrorWrap = styled.div`
  margin-bottom: 12px;
`;

const Title = styled(H2)`
  &&& {
    margin: 0 0 8px;
  }
`;

/**
 * Teams full-page PromoStandards Product Validator.
 * Shared helpers above are exported for reuse.
 */
class PSProductValidatorPage extends Component {
  constructor(props) {
    super(props);

    // Bump on each supplier/service load so late responses cannot wipe newer state.
    this._credentialsRequestId = 0;
    this._endpointRequestId = 0;

    this.state = {
      service_code: PS_DIRECTORY_SERVICE_CODES.PRODUCT_DATA,
      loadingCompanies: false,
      loadingCredentials: false,
      loadingCall: false,
      loadingFobPoints: false,
      product_id: '',
      part_id: '',
      fob_id: '',
      currency: 'USD',
      configuration_type: 'Blank',
      fob_point_choices: [],
      localization_country: 'US',
      localization_language: 'en',
      use_test_url: false,
      api_name: '',
      ps_company_code: '',
      commonsku_username: '',
      commonsku_password: '',
      serviceEndpoint: null,
      response: null,
      error: '',
      meta: null,
      request_xml: '',
      response_xml: '',
      showXml: false,
    };

    _.bindAll(this, [
      'handleChange',
      'handleSelectSupplier',
      'handleServiceChange',
      'loadServiceEndpoint',
      'loadOwnerCredentials',
      'loadFobPoints',
      'handleValidate',
      'handleCopy',
    ]);
  }

  handleChange(field, value) {
    this.setState({ [field]: value });
  }

  isPricingService() {
    return PS_DIRECTORY_SERVICE_CODES.PRICING_AND_CONFIGURATION === this.state.service_code;
  }

  getSelectedService() {
    return PS_VALIDATOR_SERVICES.find((service) => service.code === this.state.service_code)
      || PS_VALIDATOR_SERVICES[0];
  }

  handleServiceChange(serviceCode) {
    this.setState({
      service_code: serviceCode,
      serviceEndpoint: null,
      response: null,
      meta: null,
      request_xml: '',
      response_xml: '',
      fob_id: '',
      fob_point_choices: [],
    }, () => {
      const psCompanyCode = String(this.state.ps_company_code || '').trim();
      if (psCompanyCode) {
        this.loadServiceEndpoint(psCompanyCode);
      }
    });
  }

  handleSelectSupplier(promoApiName) {
    const choice = getSupplierChoices(this.props.tenant_endpoints)
      .find((c) => c.promo_api_name === promoApiName);
    if (!choice) {
      this.setState({
        api_name: '',
        ps_company_code: '',
        commonsku_username: '',
        commonsku_password: '',
        serviceEndpoint: null,
      });
      return;
    }
    // Seed username from the report row for instant UI; password often absent there.
    // Always reload from owner_credentials, then load the endpoint (do not race them).
    this.setState({
      api_name: choice.promo_api_name,
      ps_company_code: String(choice.ps_company_code || '').trim(),
      commonsku_username: choice.commonsku_username || '',
      commonsku_password: choice.commonsku_password || '',
      serviceEndpoint: null,
      response: null,
      error: '',
      fob_id: '',
      fob_point_choices: [],
    }, () => {
      this.loadOwnerCredentials(choice.promo_api_name);
    });
  }

  loadOwnerCredentials(apiName) {
    if (!apiName) {
      return;
    }
    const requestId = ++this._credentialsRequestId;
    this.setState({ loadingCredentials: true });
    oauth('POST', 'promo-standards', {
      action: 'owner_credentials',
      admin_test: true,
      api_name: apiName,
    }).then(({ json }) => {
      if (requestId !== this._credentialsRequestId) {
        return;
      }
      const creds = json?.credentials || {};
      const username = creds.commonsku_username || '';
      const password = creds.commonsku_password || '';
      const credsError = credentialsMissing(username, password)
        ? CREDENTIALS_MISSING
        : (json?.error ? String(json.error) : '');
      const psCompanyCode = String(
        creds.ps_company_code || this.state.ps_company_code || ''
      ).trim();
      this.setState({
        loadingCredentials: false,
        commonsku_username: username,
        commonsku_password: password,
        ps_company_code: psCompanyCode || this.state.ps_company_code,
        error: credsError,
      }, () => {
        if (psCompanyCode) {
          this.loadServiceEndpoint(psCompanyCode);
        } else if (!credsError) {
          this.setState({
            error: 'No ps_company_code on this supplier — cannot look up endpoint.',
          });
        }
      });
    }, ({ json }) => {
      if (requestId !== this._credentialsRequestId) {
        return;
      }
      const creds = json?.credentials || {};
      this.setState({
        loadingCredentials: false,
        commonsku_username: creds.commonsku_username || '',
        commonsku_password: creds.commonsku_password || '',
        error: json?.error || CREDENTIALS_MISSING,
      });
    });
  }

  async loadServiceEndpoint(psCompanyCode) {
    if (!psCompanyCode) {
      return;
    }
    const requestId = ++this._endpointRequestId;
    const serviceCode = this.state.service_code;
    this.setState({ loadingCompanies: true });
    try {
      const { endpoint } = await loadDirectoryEndpoint(psCompanyCode, serviceCode);
      if (requestId !== this._endpointRequestId) {
        return;
      }
      const endpointError = endpoint
        ? ''
        : `No endpoint found for company code "${psCompanyCode}".`;
      this.setState((prev) => ({
        loadingCompanies: false,
        serviceEndpoint: endpoint,
        // Do not clobber a credentials error with a successful endpoint lookup.
        error: endpointError
          || (prev.error === CREDENTIALS_MISSING ? prev.error : ''),
      }));
    } catch (e) {
      if (requestId !== this._endpointRequestId) {
        return;
      }
      this.setState({
        loadingCompanies: false,
        serviceEndpoint: null,
        error: e?.message || 'Unable to load company endpoints from PromoStandards API.',
      });
    }
  }

  loadFobPoints() {
    const {
      api_name,
      product_id,
      serviceEndpoint,
      localization_country,
      localization_language,
      use_test_url,
      commonsku_username,
      commonsku_password,
    } = this.state;

    if (credentialsMissing(commonsku_username, commonsku_password)) {
      this.setState({ error: CREDENTIALS_MISSING });
      return;
    }

    if (!api_name || !product_id.trim() || !serviceEndpoint) {
      this.setState({ error: 'Select a supplier, enter a product ID, and wait for the endpoint to load.' });
      return;
    }

    const endpointUrl = pickEndpointUrl(serviceEndpoint, use_test_url);

    this.setState({
      loadingFobPoints: true,
      error: '',
      fob_id: '',
      fob_point_choices: [],
    });

    oauth('POST', 'promo-standards', {
      action: 'fob_points',
      admin_test: true,
      api_name,
      product_id: product_id.trim(),
      endpoint_url: endpointUrl,
      endpoint_version: serviceEndpoint.version || '1.0.0',
      localization_country,
      localization_language,
    }).then(({ json }) => {
      const choices = parseFobPointChoices(json);
      this.setState({
        loadingFobPoints: false,
        fob_point_choices: choices,
        fob_id: choices[0]?.fob_id || '',
        error: json?.error || (choices.length === 0 ? 'No FOB points returned for this product.' : ''),
        request_xml: json?.request_xml || '',
        response_xml: json?.response_xml || '',
        meta: json?.meta || null,
        response: json?.fob_points ?? null,
      });
    }, ({ json }) => {
      this.setState({
        loadingFobPoints: false,
        error: json?.error || json?.message || 'Unable to load FOB points.',
      });
    });
  }

  handleValidate(e) {
    if (e) {
      e.preventDefault();
    }
    if (this.isPricingService()) {
      this.handleValidatePricing(e);
      return;
    }
    this.handleValidateProductData(e);
  }

  handleValidateProductData(e) {
    if (e) {
      e.preventDefault();
    }
    const {
      api_name,
      product_id,
      part_id,
      serviceEndpoint,
      localization_country,
      localization_language,
      use_test_url,
      commonsku_username,
      commonsku_password,
    } = this.state;

    if (credentialsMissing(commonsku_username, commonsku_password)) {
      this.setState({ error: CREDENTIALS_MISSING });
      return;
    }

    if (!api_name || !product_id.trim() || !serviceEndpoint) {
      this.setState({ error: 'Select a supplier, enter a product ID, and wait for the endpoint to load.' });
      return;
    }

    const endpointUrl = pickEndpointUrl(serviceEndpoint, use_test_url);

    this.setState({
      loadingCall: true,
      error: '',
      response: null,
      request_xml: '',
      response_xml: '',
      meta: null,
    });

    const payload = {
      action: 'product_data',
      admin_test: true,
      api_name,
      product_id: product_id.trim(),
      endpoint_url: endpointUrl,
      endpoint_version: serviceEndpoint.version || '1.0.0',
      localization_country,
      localization_language,
    };
    if (part_id.trim()) {
      payload.part_id = part_id.trim();
    }

    oauth('POST', 'promo-standards', payload).then(({ json }) => {
      this.setState({
        loadingCall: false,
        response: json?.product ?? null,
        error: json?.error || '',
        request_xml: json?.request_xml || '',
        response_xml: json?.response_xml || '',
        meta: json?.meta || null,
      });
    }, ({ json }) => {
      this.setState({
        loadingCall: false,
        error: json?.error || json?.message || 'Validation call failed.',
      });
    });
  }

  handleValidatePricing(e) {
    if (e) {
      e.preventDefault();
    }
    const {
      api_name,
      product_id,
      part_id,
      fob_id,
      currency,
      configuration_type,
      serviceEndpoint,
      localization_country,
      localization_language,
      use_test_url,
      commonsku_username,
      commonsku_password,
    } = this.state;

    if (credentialsMissing(commonsku_username, commonsku_password)) {
      this.setState({ error: CREDENTIALS_MISSING });
      return;
    }

    if (!api_name || !product_id.trim() || !fob_id.trim() || !serviceEndpoint) {
      this.setState({
        error: 'Select a supplier, enter product ID and FOB, then wait for the endpoint to load.',
      });
      return;
    }

    const endpointUrl = pickEndpointUrl(serviceEndpoint, use_test_url);
    const trimmedPartId = part_id.trim();

    this.setState({
      loadingCall: true,
      error: '',
      response: null,
      request_xml: '',
      response_xml: '',
      meta: null,
    });

    const pricingPayload = {
      action: 'pricing_configuration',
      admin_test: true,
      api_name,
      product_id: product_id.trim(),
      fob_id: fob_id.trim(),
      currency,
      configuration_type,
      endpoint_url: endpointUrl,
      endpoint_version: serviceEndpoint.version || '1.0.0',
      localization_country,
      localization_language,
    };
    if (trimmedPartId) {
      pricingPayload.part_id = trimmedPartId;
    }

    oauth('POST', 'promo-standards', pricingPayload).then(({ json }) => {
      this.setState({
        loadingCall: false,
        response: json?.configuration ?? null,
        error: json?.error || '',
        request_xml: json?.request_xml || '',
        response_xml: json?.response_xml || '',
        meta: json?.meta || null,
      });
    }, ({ json }) => {
      this.setState({
        loadingCall: false,
        error: json?.error || json?.message || 'Validation call failed.',
      });
    });
  }

  handleCopy(text) {
    if (!text || !navigator.clipboard) {
      return;
    }
    navigator.clipboard.writeText(text);
  }

  renderCopyField(value, disabled = false) {
    return (
      <CopyRow>
        <Input type="text" readOnly value={value || ''} />
        <Button
          type="button"
          size="small"
          disabled={disabled || !value}
          onClick={() => this.handleCopy(value)}
        >
          Copy
        </Button>
      </CopyRow>
    );
  }

  render() {
    const { onBack } = this.props;
    const suppliers = getSupplierChoices(this.props.tenant_endpoints);
    const isPricingService = this.isPricingService();
    const {
      service_code,
      api_name,
      product_id,
      part_id,
      fob_id,
      currency,
      configuration_type,
      fob_point_choices,
      localization_country,
      localization_language,
      use_test_url,
      commonsku_username,
      commonsku_password,
      serviceEndpoint,
      loadingCompanies,
      loadingCredentials,
      loadingCall,
      loadingFobPoints,
      response,
      error,
      meta,
      request_xml,
      response_xml,
      showXml,
    } = this.state;

    const endpointUrl = pickEndpointUrl(serviceEndpoint, use_test_url);
    const cannotValidate = loadingCall
      || !serviceEndpoint
      || !product_id.trim()
      || credentialsMissing(commonsku_username, commonsku_password)
      || (isPricingService && !fob_id.trim());
    const cannotLoadFob = loadingFobPoints
      || loadingCall
      || !serviceEndpoint
      || !product_id.trim()
      || credentialsMissing(commonsku_username, commonsku_password);
    const selectedFobChoice = fob_point_choices.find((choice) => choice.fob_id === fob_id);
    const selectedFobLocation = selectedFobChoice?.label?.includes(' — ')
      ? selectedFobChoice.label.split(' — ')[1]
      : '';

    return (
      <Page>
        <PageHeader>
          <div>
            <Title>PromoStandards Product Validator</Title>
            <Subtitle>
              For more details, use the{' '}
              <a
                href="https://promostandards.org/web-service-validator/"
                target="_blank"
                rel="noopener noreferrer"
              >
                PromoStandards Web Service Validator
              </a>
              .
            </Subtitle>
          </div>
          <Button type="button" secondary onClick={onBack}>
            Back to report
          </Button>
        </PageHeader>

        <Row>
          <Col xs sm={6} padded>
            <FieldStack>
              <Label>Service</Label>
              <NativeSelect
                value={service_code}
                onChange={(e) => this.handleServiceChange(e.target.value)}
                disabled={loadingCall || loadingFobPoints}
              >
                {PS_VALIDATOR_SERVICES.map((service) => (
                  <option key={service.code} value={service.code}>
                    {service.label}
                  </option>
                ))}
              </NativeSelect>
            </FieldStack>
          </Col>

          <Col xs sm={6} padded>
            <FieldStack>
              <Label>Supplier</Label>
              <NativeSelect
                value={api_name}
                onChange={(e) => this.handleSelectSupplier(e.target.value)}
                disabled={loadingCall || loadingFobPoints}
              >
                <option value="">Select…</option>
                {suppliers.map((s) => (
                  <option key={s.promo_api_name} value={s.promo_api_name}>
                    {s.label} ({s.promo_api_name})
                  </option>
                ))}
              </NativeSelect>
            </FieldStack>
          </Col>

          <Col xs sm={3} padded>
            <FieldStack>
              <Label>
                commonsku username{' '}
                {loadingCredentials ? <Hint>(loading…)</Hint> : null}
              </Label>
              {this.renderCopyField(commonsku_username)}
            </FieldStack>
          </Col>

          <Col xs sm={3} padded>
            <FieldStack>
              <Label>commonsku password</Label>
              {this.renderCopyField(commonsku_password)}
            </FieldStack>
          </Col>

          <Col xs padded>
            <FieldStack>
              <Label>
                Endpoint{' '}
                {loadingCompanies ? <Hint>(loading…)</Hint> : null}
              </Label>
              {this.renderCopyField(endpointUrl, !endpointUrl)}
              {serviceEndpoint ? (
                <Meta>
                  Version {serviceEndpoint.version} · status {serviceEndpoint.status}
                  {serviceEndpoint.source ? ` · source ${serviceEndpoint.source}` : ''}
                  {' · '}
                  <LabeledCheckbox
                    label="use test URL"
                    checked={use_test_url}
                    onChange={() => this.handleChange('use_test_url', !use_test_url)}
                  />
                </Meta>
              ) : null}
            </FieldStack>
          </Col>

          <Col xs sm={4} padded>
            <FieldStack>
              <Label>Product ID</Label>
              <Input
                type="text"
                value={product_id}
                placeholder="Supplier productId"
                onChange={(e) => this.handleChange('product_id', e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') this.handleValidate(e); }}
                disabled={loadingCall || loadingFobPoints}
              />
            </FieldStack>
          </Col>

          <Col xs sm={4} padded>
            <FieldStack>
              <Label>
                Part ID <Hint>(optional)</Hint>
              </Label>
              <Input
                type="text"
                value={part_id}
                placeholder="Supplier partId"
                onChange={(e) => this.handleChange('part_id', e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') this.handleValidate(e); }}
                disabled={loadingCall || loadingFobPoints}
              />
            </FieldStack>
          </Col>

          {isPricingService ? (
            <>
              <Col xs sm={2} padded>
                <FieldStack>
                  <Label>Currency</Label>
                  <NativeSelect
                    value={currency}
                    onChange={(e) => this.handleChange('currency', e.target.value)}
                    disabled={loadingCall || loadingFobPoints}
                  >
                    {PS_PPC_CURRENCIES.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </NativeSelect>
                </FieldStack>
              </Col>

              <Col xs sm={2} padded>
                <FieldStack>
                  <Label>Configuration</Label>
                  <NativeSelect
                    value={configuration_type}
                    onChange={(e) => this.handleChange('configuration_type', e.target.value)}
                    disabled={loadingCall || loadingFobPoints}
                  >
                    {PS_PPC_CONFIGURATION_TYPES.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </NativeSelect>
                </FieldStack>
              </Col>
            </>
          ) : null}

          <Col xs sm={2} padded>
            <FieldStack>
              <Label>Country</Label>
              <Input
                type="text"
                value={localization_country}
                onChange={(e) => this.handleChange('localization_country', e.target.value)}
                disabled={loadingCall || loadingFobPoints}
              />
            </FieldStack>
          </Col>

          <Col xs sm={2} padded>
            <FieldStack>
              <Label>Language</Label>
              <Input
                type="text"
                value={localization_language}
                onChange={(e) => this.handleChange('localization_language', e.target.value)}
                disabled={loadingCall || loadingFobPoints}
              />
            </FieldStack>
          </Col>
        </Row>

        {isPricingService ? (
          <Row>
            <Col xs sm={3} padded>
              <FieldStack>
                <Label>
                  FOB{loadingFobPoints ? <Hint> …</Hint> : null}
                </Label>
                <FobLoadRow>
                  <FobSelect
                    value={fob_id}
                    onChange={(e) => this.handleChange('fob_id', e.target.value)}
                    disabled={loadingCall || loadingFobPoints || fob_point_choices.length === 0}
                  >
                    <option value="">—</option>
                    {fob_point_choices.map((choice) => (
                      <option key={choice.fob_id} value={choice.fob_id}>
                        {choice.fob_id}
                      </option>
                    ))}
                  </FobSelect>
                  <Button
                    type="button"
                    size="small"
                    secondary
                    onClick={this.loadFobPoints}
                    disabled={cannotLoadFob}
                  >
                    {loadingFobPoints ? '…' : 'Load'}
                  </Button>
                </FobLoadRow>
                {selectedFobLocation ? <Meta>{selectedFobLocation}</Meta> : null}
              </FieldStack>
            </Col>
          </Row>
        ) : null}

        <Row>
          <Col xs padded>
            <FieldStack>
              <Button
                type="button"
                onClick={this.handleValidate}
                disabled={cannotValidate}
              >
                {loadingCall ? 'Validating…' : 'Validate'}
              </Button>
            </FieldStack>
          </Col>
        </Row>

        {error ? (
          <ErrorWrap>
            <AlertNotification alertType="error">
              {error}
            </AlertNotification>
          </ErrorWrap>
        ) : null}

        {meta ? (
          <Meta $spaced>
            Endpoint: {meta.endpoint_url}
            {meta.endpoint_version ? ` · version ${meta.endpoint_version}` : ''}
            {meta.price_type ? ` · price type ${meta.price_type}` : ''}
          </Meta>
        ) : null}

        <ResponseHeader>
          <Text as="strong">Response</Text>
          <LabeledCheckbox
            label="show SOAP XML"
            checked={showXml}
            onChange={() => this.handleChange('showXml', !showXml)}
          />
        </ResponseHeader>

        {loadingCall ? (
          <LoadingWrap>
            <Loading />
          </LoadingWrap>
        ) : (
          <ResponsePre>
            {formatSoapOrJsonResponse({
              showXml,
              response,
              requestXml: request_xml,
              responseXml: response_xml,
            })}
          </ResponsePre>
        )}
      </Page>
    );
  }
}

const mapStateToProps = (state) => ({
  tenant_endpoints: state.entities.tenant_endpoints || {},
});

export default connect(mapStateToProps)(PSProductValidatorPage);
